/**
 * @author: Wangwei
 * @description: 打卡营学习页面
 */
import { useLocation, useRequest, useParams, Helmet, history, useModel } from 'umi';
import styles from './css/studyIndexCSS.less';
import { useState, useEffect } from 'react';
import cx from 'classnames';
import { Menu, Flex, Progress, message } from 'antd';
import { getNodeProgress, getSignUp } from '@/utils/requests/camp/requests_study';
import { getStudyNode } from '@/utils/requests/camp/requests_details';
import { StudyInfoType } from '@/types/camp/study/studentBaseInfoType';
import { NodeStudyState, CampStudyState } from '@/types/camp/study/index';
import { ProgressType, StudyNode } from '@/types/camp/details/detailsType';
import { CheckCircleFilled, LeftOutlined } from '@ant-design/icons';
import StudyEdit from './components/study/studyEdit';
import StudyPreview from './components/study/studyPreview';
import StudyEditPreview from './components/study/studyEditPreview';
import NeedLogin from '@/components/isLogin';
import EventTracking from '@/pages/camp/components/tracking/EventTracking';
import { postStudyNodeLog } from '@/utils/requests/camp/requests_burying_point';

export default function CampStudy() {
  const [newTitle, setNewTitle] = useState<string>();
  const { id: signUpId } = useParams() as { id: string };
  const [studentInfo, setStudentInfo] = useState<StudyInfoType>();
  const [nodeProgress, setNodeProgress] = useState<ProgressType>();
  const [nodeData, setNodeData] = useState<StudyNode[]>([]);
  const [findNodeData, setFindNodeData] = useState<StudyNode>();
  const location = useLocation();
  const [nodeId, setNodeId] = useState<string>('');
  const [nodeLogId, setNodeLogId] = useState<any>(null);
  const { initialState } = useModel('@@initialState');

  // 学习节点埋点
  const { run: postStudyNodeLogRequest } = useRequest(
    (durationTime) => {
      const parame = {
        batchId: studentInfo?.batchId,
        nodeId: nodeId,
        signUpId: signUpId,
        id: nodeLogId || null,
        durationTime: durationTime / 1000,
      };

      return postStudyNodeLog(parame);
    },
    {
      manual: true,
      onSuccess: (res: any) => {
        if (!nodeLogId) {
          setNodeLogId(res.id);
        }
      },
    },
  );

  useEffect(() => {
    if (studentInfo?.batchId) {
      const durationTime = 20000;
      const submitTimer = () => {
        postStudyNodeLogRequest(durationTime);
      };
      const intervalId = setInterval(submitTimer, durationTime);
      return () => clearInterval(intervalId);
    }
  }, [studentInfo?.batchId]);

  // 获取报名数据
  const { run: getSignUpRequest } = useRequest(
    (signUpId) => {
      return getSignUp(signUpId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        if (res.createdBy !== initialState?.uid) {
          message.warning('请先报名打卡营');
          setTimeout(function () {
            history.push(`/v2/camp/${res.cardCampId}`);
          }, 1000);
        } else {
          setStudentInfo(res);
        }
      },
    },
  );
  // 获取进度
  const { run: getNodeProgressRequest } = useRequest(
    (signUpId) => {
      return getNodeProgress(signUpId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setNodeProgress(res);
      },
    },
  );
  // 获取节点
  const { run: getStudyNodeRequest } = useRequest(
    (signUpId) => {
      return getStudyNode(signUpId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        if (nodeId) {
          const find = res.find((item: any) => item.id === nodeId);
          find && setFindNodeData(find);
        }
        setNodeData(res);
      },
    },
  );

  useEffect(() => {
    if (nodeId) {
      const find = nodeData.find((item: any) => item.id === nodeId);
      find && setFindNodeData(find);
    }
  }, [nodeId]);

  useEffect(() => {
    if (signUpId) {
      getSignUpRequest(signUpId);
      getNodeProgressRequest(signUpId);
      getStudyNodeRequest(signUpId);
    }
  }, [signUpId]);

  useEffect(() => {
    // @ts-ignore
    setNodeId(location.query?.nodeid);
  }, [location]);

  useEffect(() => {
    setNewTitle(studentInfo?.simpleCamp.name);
  }, [studentInfo?.simpleCamp.name]);

  const handlePercent = () => {
    if (nodeProgress) {
      return (nodeProgress.successNode / nodeProgress.allNode) * 100;
    } else {
      return 0;
    }
  };

  return (
    <>
      <NeedLogin />
      {newTitle && (
        <Helmet>
          <title>{`${newTitle}- 打卡营 - 帆软学院`}</title>
        </Helmet>
      )}

      <div className={cx(styles.camp_study_index_box)}>
        <div className={styles.lf}>
          <a
            href={`/v2/camp/class/${studentInfo?.batchId}`}
            className={cx(styles.go_back_details, 'col172 hover008')}
          >
            <span>
              <LeftOutlined />
            </span>
            <span>返回</span>
          </a>

          <div className={styles.st_node_progress}>
            <div className="flex_3">
              <span className="hide_txt" style={{ width: 220 }}>
                {studentInfo?.simpleCamp.name}
              </span>
              <span className="col808">
                {nodeProgress?.successNode}/{nodeProgress?.allNode}
              </span>
            </div>
            <div style={{ width: '100%' }}>
              <Flex gap="small" vertical>
                <Progress percent={handlePercent()} showInfo={false} />
              </Flex>
            </div>
          </div>
          <Menu
            className={styles.study_menu_box}
            style={{ width: '100%', padding: '0 10px', borderInlineEnd: 0 }}
            selectedKeys={[nodeId]}
            defaultOpenKeys={['sub1']}
            mode="inline"
            items={[
              {
                key: 'sub1',
                type: 'group',
                children: nodeData.map((item) => ({
                  key: item.id,
                  onClick: () => {
                    setNodeId(item.id);
                    history.push(`?nodeid=${item.id}`);
                  },
                  label: (
                    <>
                      <div className="flex_3 f16">
                        <span>{item.name}</span>
                        {item.studyState === NodeStudyState.NODE_SUCCESS && (
                          <CheckCircleFilled className="menu_icon" />
                        )}
                      </div>
                    </>
                  ),
                })),
              },
            ]}
          />
        </div>

        <div className={styles.rt}>
          {findNodeData?.studyState == NodeStudyState.NOT_START_LEARNING ||
          findNodeData?.studyState == NodeStudyState.STUDYING ? (
            <StudyEdit nodeId={nodeId} />
          ) : findNodeData?.studyState == NodeStudyState.NODE_FAIL ? (
            <StudyEditPreview nodeId={nodeId} />
          ) : (
            <StudyPreview
              key={`preview_${nodeId}`}
              nodeId={nodeId}
              studentInfo={studentInfo}
              nodeData={nodeData}
              nodeProgress={nodeProgress}
            />
          )}
        </div>
      </div>
      <EventTracking />
    </>
  );
}
